import { DomainSpecificTrackingCodesProvider } from "../tracking_codes/DomainSpecificTrackingCodesProvider";
import { DefaultPageTypeProvider } from "../utilities/page_type/DefaultPageTypeProvider";
import { PageTypeProvider } from "../utilities/page_type/PageTypeProvider";
import { DefaultQueryStringValuesAccessor } from "../utilities/query_string/DefaultQueryStringValuesAccessor";
import { QueryStringValuesAccessor } from "../utilities/query_string/QueryStringValuesAccessor";
import { ServiceProvider } from "./ServiceProvider";
import { WindowLocationWrapper } from "../utilities/window_wrapping/WindowLocationWrapper";
import { DefaultWindowLocationWrapper } from "../utilities/window_wrapping/DefaultWindowLocationWrapper";
import { ScopedTrackingPermissionChecker } from "../utilities/tracking_permissions/ScopedTrackingPermissionChecker";
import { createScopedPermissionsChecker } from "../utilities/tracking_permissions/ScopedTrackingPermissionsCheckerFactory";
import { TrackingCodesProvider } from "../tracking_codes/TrackingCodesProvider";
import { TrafficInfoProvider } from "../utilities/traffic_info/TrafficInfoProvider";
import { DefaultTrafficInfoProvider } from "../utilities/traffic_info/DefaultTrafficInfoProvider";
import { InternalEventsSender } from "../utilities/internal_events/InternalEventsSender";
import { DefaultInternalEventsSender } from "../utilities/internal_events/DefaultInternalEventsSender";
import { AnalyticsPageNameProvider } from "../utilities/page_name/AnalyticsPageNameProvider";
import { DefaultAnalyticsPageNameProvider } from "../utilities/page_name/DefaultAnalyticsPageNameProvider";
import { UserLicenseStatusChecker } from "../utilities/user_license/UserLicenseStatusChecker";
import { DefaultUserLicenseStatusChecker } from "../utilities/user_license/DefaultUserLicenseStatusChecker";
import { DEFAULT_BASE_URL } from "../utilities/internal_events/InternalEventsBaseUrls";
import { InternalPageviewAutomaticTrackingChecker } from "../utilities/internal_events/InternalPageviewAutomaticTrackingChecker";
import { DefaultInternalPageviewAutomaticTrackingChecker } from "../utilities/internal_events/DefaultInternalPageviewAutomaticTrackingChecker";
import { ExecutionContextsEqualityComparer } from "../utilities/execution_contexts_comparison/ExecutionContextsEqualityComparer";
import { DefaultExecutionContextsEqualityComparer } from "../utilities/execution_contexts_comparison/DefaultExecutionContextsEqualityComparer";
import { DocumentWrapper } from "../utilities/document_wrapping/DocumentWrapper";
import { DefaultDocumentWrapper } from "../utilities/document_wrapping/DefaultDocumentWrapper";
import { GoogleAnalyticsDimensionsAndMetricsProvider } from "../utilities/analytics_parameters/GoogleAnalyticsDimentionsAndMetricsProvider";
import { DefaultGoogleAnalyticsDimensionsAndMetricsProvider } from "../utilities/analytics_parameters/DefaultGoogleAnalyticsDimensionsAndMetricsProvider";
import { GoogleAnalyticsTrackerFactory } from "../trackers/google/GoogleAnalyticsTrackerFactory";
import { DefaultGoogleAnalyticsTrackerFactory } from "../trackers/google/DefaultGoogleAnalyticsTrackerFactory";

export class DefaultServiceProvider implements ServiceProvider {
	getWindowLocationWrapper(): WindowLocationWrapper {
		return new DefaultWindowLocationWrapper();
	}

	getDocumentWrapper(): DocumentWrapper {
		return new DefaultDocumentWrapper(this.getWindowLocationWrapper());
	}

	getPageTypeProvider(): PageTypeProvider {
		return new DefaultPageTypeProvider();
	}

	getQueryStringValuesAccessor(): QueryStringValuesAccessor {
		return new DefaultQueryStringValuesAccessor(
			this.getWindowLocationWrapper()
		);
	}

	getScopedTrackingPermissionChecker(): ScopedTrackingPermissionChecker {
		return createScopedPermissionsChecker(
			this.getWindowLocationWrapper(),
			this.getQueryStringValuesAccessor()
		);
	}

	getTrackingCodesProvider(): TrackingCodesProvider {
		return new DomainSpecificTrackingCodesProvider(
			this.getWindowLocationWrapper()
		);
	}

	getTrafficInfoProvider(): TrafficInfoProvider {
		return new DefaultTrafficInfoProvider();
	}

	getInternalEventsSender(baseUrl = DEFAULT_BASE_URL): InternalEventsSender {
		return new DefaultInternalEventsSender(baseUrl);
	}

	getAnalyticsPageNameProvider(): AnalyticsPageNameProvider {
		return new DefaultAnalyticsPageNameProvider(
			this.getWindowLocationWrapper()
		);
	}

	getUserLicenseStatusChecker(): UserLicenseStatusChecker {
		return new DefaultUserLicenseStatusChecker();
	}

	getInternalPageviewAutomaticTrackingChecker(): InternalPageviewAutomaticTrackingChecker {
		return new DefaultInternalPageviewAutomaticTrackingChecker();
	}

	getExecutionContextsEqualityComparer(): ExecutionContextsEqualityComparer {
		return new DefaultExecutionContextsEqualityComparer();
	}

	getGoogleAnalyticsDimensionsAndMetricsProvider(): GoogleAnalyticsDimensionsAndMetricsProvider {
		return new DefaultGoogleAnalyticsDimensionsAndMetricsProvider(this);
	}

	getGoogleAnalyticsTrackerFactory(): GoogleAnalyticsTrackerFactory {
		return new DefaultGoogleAnalyticsTrackerFactory(this);
	}
}
